import React, { useContext } from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';
import { useSelector } from 'react-redux';

import { black, gray3, gray4, warning, white } from 'styles/global_defaults/colors';
import { topHeaderHeight } from 'styles/global_defaults/scaffolding';

// hooks
import useCourseHomeUtils from 'shared/hooks/use-course-home-utils';
import { useLecturePageFromParams, useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';

// components
import { LecturePageMode } from 'lecture_pages/components';
import { linkTolecturePage } from 'lecture_pages/components/lecture-page-routes';
import { AngularServicesContext } from 'react-app';
import { SyncingStatus } from 'redux/schemas/models/lecture-page';
import { getCurrentCourse } from 'redux/selectors/course';
import TopPanelActionButton from './top-panel-action-button';
import { config } from '@config/config.json';

const LecturePageEditButton = () => {
  const styles = css`
    .edit-icon {
      background-color: ${black};
      width: ${topHeaderHeight}px;
      height: ${topHeaderHeight}px;
      color: ${white};

      &:hover {
        background-color: ${warning};
      }
    }

    .disabled-edit-icon {
      background-color: ${gray4} !important;
      width: ${topHeaderHeight}px;
      height: ${topHeaderHeight}px;
      color: ${gray3};
      cursor: not-allowed;

      &:hover {
        background-color: ${gray4};
      }
    }
  `;

  const params = useLecturePageParams();
  const lecturePage = useLecturePageFromParams();
  const { $state, CurrentPermissionsManager } = useContext(AngularServicesContext);
  const { isEditOrReorderMode } = useCourseHomeUtils();
  const { isContentManagementCollection } = useSelector(getCurrentCourse);

  const showEditIcon = $state.current.parent === 'lecture-page-parent'
    && (
      CurrentPermissionsManager.isInstructor()
      || CurrentPermissionsManager.isCourseBuilder()
      || CurrentPermissionsManager.isConfigAndRegistrationRole()
    );

  const handleEditClick = () => {
    const { catalogId, lecturePageId } = params;

    const newPath = linkTolecturePage({
      catalogId,
      lecturePageId,
      mode: LecturePageMode.EDIT,
    });

    window.location.hash = `!/courses${newPath}`;
  };

  const disableEditIcon = SyncingStatus.SYNC_IN_PROGRESS === lecturePage?.syncingStatus;
  const enabledEditIconText = CurrentPermissionsManager.isCourseBuilder()
    ? t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.LECTURE_LIST.LESSON_ACTION.EDIT_LESSON()
    : t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.ENTER_ADMIN_MODE();

  return (
    <React.Fragment>
      {showEditIcon && (
        <div css={styles} className={`ml-2 ${isContentManagementCollection ? 'mt-1' : ''}`}>
          <TopPanelActionButton
            icon='edit'
            tooltipText={disableEditIcon
              ? t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.EDIT_ICON_DISABLED_TEXT()
              : enabledEditIconText}
            useDefaultStyle={false}
            className={disableEditIcon ? 'disabled-edit-icon' : 'edit-icon'}
            tooltipPlacement='bottom-end'
            ariaLabel={disableEditIcon
              ? t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.EDIT_ICON_DISABLED_TEXT()
              : enabledEditIconText}
            dataQa={config.pendo.lectureEdit.editMenuTopPanel.editLessonOption}
            onClick={handleEditClick}
            disabled={disableEditIcon}
            isFocusable={!isEditOrReorderMode}
          />
        </div>
      )}
    </React.Fragment>
  );
};

export default LecturePageEditButton;
